import { FC } from 'react';
import { useScrollAnimation } from './UseScrollAnimation';

interface Station {
    airport: string;
    stationNumber: number;
}

interface Terminal {
    _id: string;
    code: string;
    name: string;
}

interface RemarkCode {
    key: string;
    description: string;
}

interface FlightData {
    _id: string;
    flightNumber: string;
    scheduledTravelTime: string;
    airline: string;
    airportCode: string;
    airportId: string;
    checkInDesks: string[];
    checkInRow: string[];
    codeShareFlight: string[];
    effectiveTime: string;
    flightType: string;
    isDeleted: boolean;
    isDisplay: boolean;
    serviceTypeCode: string;
    stations: Station[];
    terminalId: string;
    tripType: string;
    loungeOpeningTime: string;
    remarkCode: RemarkCode;
    loungeBoardingTime: string;
    terminal: Terminal;
}

interface FlightNumberCellProps {
    flight: FlightData;
    globalStartTime: number;
    rowHeight: number;
    showInline?: boolean;
}

const FlightNumberCell: FC<FlightNumberCellProps> = ({
    flight,
    globalStartTime,
    rowHeight,
    showInline = false,
}) => {
    const mainFlightNumber = flight.flightNumber;
    const codeShareFlights = flight.codeShareFlight?.filter(Boolean) || [];

    const shouldAnimate = showInline
        ? codeShareFlights.length > 0
        : codeShareFlights.length > 1;

    const displayItems = shouldAnimate ? (() => {
        if (showInline) {
            const combinedItems = [mainFlightNumber, ...codeShareFlights];
            return [...combinedItems, ...combinedItems];
        } else {
            return [...codeShareFlights, ...codeShareFlights];
        }
    })() : [];

    const { listRef } = useScrollAnimation({
        items: displayItems,
        globalStartTime,
        itemHeight: rowHeight - 2,
        scrollDuration: 1.5,
        animationPrefix: 'flight',
        enabled: shouldAnimate
    });

    return (
        <div className="scroll-wrapper">
            {showInline ? (
                shouldAnimate ? (
                    <ul className="scroll-item-list" ref={listRef}>
                        {displayItems.map((item, i) => {
                            const originalLength = codeShareFlights.length + 1;
                            const isMainFlightNumber = (i % originalLength) === 0;
                            return (
                                <li
                                    style={{ height: (rowHeight - 2) + "px" }}
                                    className="scroll-item"
                                    key={i}
                                >
                                    &nbsp;<span className={isMainFlightNumber ? "underline" : ""}>{item}</span>
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <div
                        style={{ height: (rowHeight - 2) + "px" }}
                        className="scroll-item"
                    >
                        &nbsp;{mainFlightNumber}
                    </div>
                )
            ) : (
                <div className='row'>
                    <div style={{ lineHeight: "50px" }} className='col-md-6'>
                        <label>
                            &nbsp;{mainFlightNumber}
                        </label>
                        {codeShareFlights.length > 0 && <span> | </span>}
                    </div>

                    <div className='col-md-6 p-0'>
                        {shouldAnimate ? (
                            <ul style={{ position: "relative" }} className="scroll-item-list" ref={listRef}>
                                {displayItems.map((item, i) => (
                                    <li
                                        style={{ height: (rowHeight - 2) + "px" }}
                                        className="scroll-item"
                                        key={i}
                                    >
                                        &nbsp;{item}
                                    </li>
                                ))}
                            </ul>
                        ) : codeShareFlights.length === 1 ? (
                            <div
                                style={{ height: (rowHeight - 2) + "px" }}
                                className="scroll-item"
                            >
                                &nbsp;{codeShareFlights[0]}
                            </div>
                        ) : null}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FlightNumberCell;